import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpdateTicketsOptions,
  BulkUpdateTicketsResponse,
  CheckInTicketResponse,
  CheckInTicketsOptions,
  DeleteTicketCheckInResponse,
  DeleteTicketCheckInsOptions,
  GetTicketIdentifiers,
  GetTicketOptions,
  ListTicketsOptions,
  ListTicketsResponse,
  TicketOrderUpdatedEnvelope,
  TicketingTicket,
  UpdateTicketIdentifiers,
  UpdateTicketOptions,
  UpdateTicketResponse,
  bulkUpdateTickets as universalBulkUpdateTickets,
  checkInTickets as universalCheckInTickets,
  deleteTicketCheckIns as universalDeleteTicketCheckIns,
  getTicket as universalGetTicket,
  listTickets as universalListTickets,
  updateTicket as universalUpdateTicket,
} from './events-v1-ticket-tickets.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

type TicketNonNullablePaths =
  | `ticketNumber`
  | `orderNumber`
  | `ticketDefinitionId`
  | `name`
  | `price.amount`
  | `price.currency`
  | `free`
  | `policy`
  | `orderStatus`
  | `orderArchived`
  | `orderFullName`
  | `guestDetails.guestAssigned`
  | `guestDetails.form.inputValues`
  | `guestDetails.form.inputValues.${number}.inputName`
  | `guestDetails.form.inputValues.${number}.value`
  | `archived`
  | `anonymized`
  | `checkInUrl`
  | `ticketPdfUrl`
  | `channel`
  | `walletPassUrl`;

export function listTickets(httpClient: HttpClient): ListTicketsSignature {
  return (eventId: string[], options?: ListTicketsOptions) =>
    universalListTickets(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListTicketsSignature {
  /**
   * Retrieves a list of up to 100 tickets.
   *
   * <!--
   * >**Note:** This function is restricted and only runs if you elevate permissions using the [wix-auth.elevate()](https://www.wix.com/velo/reference/wix-auth/elevate) function.
   * -->
   * @param - Event IDs to which the tickets belong.
   * @param - Options for defining the returned list of tickets.
   */
  (eventId: string[], options?: ListTicketsOptions): Promise<
    NonNullablePaths<
      ListTicketsResponse,
      | `total`
      | `offset`
      | `limit`
      | {
          [P in TicketNonNullablePaths]: `tickets.${number}.${P}`;
        }[TicketNonNullablePaths]
    >
  >;
}

export function getTicket(httpClient: HttpClient): GetTicketSignature {
  return (
    identifiers: NonNullablePaths<
      GetTicketIdentifiers,
      `eventId` | `ticketNumber`
    >,
    options?: GetTicketOptions
  ) =>
    universalGetTicket(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetTicketSignature {
  /**
   * Retrieves a ticket by the unique ticket number.
   * @param - Details for the ticket to retrieve.
   * @param - Options for the returned ticket data.
   * @returns Ticket.
   */
  (
    identifiers: NonNullablePaths<
      GetTicketIdentifiers,
      `eventId` | `ticketNumber`
    >,
    options?: GetTicketOptions
  ): Promise<NonNullablePaths<TicketingTicket, TicketNonNullablePaths>>;
}

export function checkInTickets(
  httpClient: HttpClient
): CheckInTicketsSignature {
  return (eventId: string, options?: CheckInTicketsOptions) =>
    universalCheckInTickets(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CheckInTicketsSignature {
  /**
   * Checks in 1 or more tickets.
   * @param - Event ID to which the ticket belongs.
   * @param - Options for tickets to check-in.
   */
  (eventId: string, options?: CheckInTicketsOptions): Promise<
    NonNullablePaths<
      CheckInTicketResponse,
      {
        [P in TicketNonNullablePaths]: `tickets.${number}.${P}`;
      }[TicketNonNullablePaths]
    >
  >;
}

export function deleteTicketCheckIns(
  httpClient: HttpClient
): DeleteTicketCheckInsSignature {
  return (eventId: string, options?: DeleteTicketCheckInsOptions) =>
    universalDeleteTicketCheckIns(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteTicketCheckInsSignature {
  /**
   * Deletes check-ins for 1 or more tickets.
   * @param - Event ID to which the ticket belongs.
   * @param - Options for tickets to delete.
   */
  (eventId: string, options?: DeleteTicketCheckInsOptions): Promise<
    NonNullablePaths<
      DeleteTicketCheckInResponse,
      {
        [P in TicketNonNullablePaths]: `tickets.${number}.${P}`;
      }[TicketNonNullablePaths]
    >
  >;
}

export function updateTicket(httpClient: HttpClient): UpdateTicketSignature {
  return (
    identifiers: NonNullablePaths<
      UpdateTicketIdentifiers,
      `eventId` | `ticketNumber`
    >,
    options?: UpdateTicketOptions
  ) =>
    universalUpdateTicket(
      identifiers,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateTicketSignature {
  /**
   * Updates a ticket.
   * @param - Details for the ticket to update.
   * @param - Ticket details to update.
   */
  (
    identifiers: NonNullablePaths<
      UpdateTicketIdentifiers,
      `eventId` | `ticketNumber`
    >,
    options?: UpdateTicketOptions
  ): Promise<
    NonNullablePaths<
      UpdateTicketResponse,
      { [P in TicketNonNullablePaths]: `ticket.${P}` }[TicketNonNullablePaths]
    >
  >;
}

export function bulkUpdateTickets(
  httpClient: HttpClient
): BulkUpdateTicketsSignature {
  return (eventId: string, options?: BulkUpdateTicketsOptions) =>
    universalBulkUpdateTickets(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateTicketsSignature {
  /**
   * Archives multiple tickets.
   * @param - Options for updating the tickets.
   * @param - Event ID to which the ticket belongs.
   */
  (eventId: string, options?: BulkUpdateTicketsOptions): Promise<
    NonNullablePaths<
      BulkUpdateTicketsResponse,
      {
        [P in TicketNonNullablePaths]: `tickets.${number}.${P}`;
      }[TicketNonNullablePaths]
    >
  >;
}

export const onTicketOrderUpdated = EventDefinition(
  'wix.events.ticketing.events.OrderUpdated',
  false,
  (event: TicketOrderUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.created' },
            { path: 'data.updated' },
            { path: 'data.checkoutForm.inputValues.dateTime' },
            { path: 'data.tickets.checkIn.created' },
            { path: 'data.tickets.form.inputValues.dateTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.checkoutForm.inputValues.number' },
            { path: 'data.tickets.form.inputValues.number' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.checkoutForm.inputValues.address.address' },
            { path: 'data.tickets.form.inputValues.address.address' },
          ],
        },
      ])
    )
)<TicketOrderUpdatedEnvelope>();

export {
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BaseEventMetadata,
  BulkUpdateTicketsOptions,
  BulkUpdateTicketsRequest,
  BulkUpdateTicketsResponse,
  ChannelType,
  CheckIn,
  CheckInTicketRequest,
  CheckInTicketResponse,
  CheckInTicketsOptions,
  Counts,
  DeleteTicketCheckInRequest,
  DeleteTicketCheckInResponse,
  DeleteTicketCheckInsOptions,
  FacetCounts,
  FormResponse,
  FormattedAddress,
  GetDemoTicketRequest,
  GetDemoTicketResponse,
  GetTicketIdentifiers,
  GetTicketOptions,
  GetTicketRequest,
  GetTicketResponse,
  GuestDetails,
  GuestDetailsUpdate,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  ListTicketsOptions,
  ListTicketsRequest,
  ListTicketsResponse,
  MessageEnvelope,
  Money,
  OnlineConferencingLogin,
  OrderStatus,
  OrderUpdated,
  StandardDetails,
  State,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  Ticket,
  TicketDetails,
  TicketFacetCounts,
  TicketFacets,
  TicketFieldset,
  TicketOrderUpdatedEnvelope,
  TicketingTicket,
  UpdateTicketIdentifiers,
  UpdateTicketOptions,
  UpdateTicketRequest,
  UpdateTicketResponse,
  WebhookIdentityType,
} from './events-v1-ticket-tickets.universal.js';
